import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box, Button, Alert, CircularProgress, Paper,
  Table, TableHead, TableBody, TableRow, TableCell, TableContainer, IconButton, Tooltip, Chip,
  TableSortLabel, Tabs, Tab,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import VisibilityIcon from "@mui/icons-material/Visibility";
import DownloadIcon from "@mui/icons-material/Download";
import { useNavigate } from "react-router-dom";
import DashboardLayout from "../../../layouts/DashboardLayout";
import PageHeader from "../../../components/layout/PageHeader";
import axios from "../../../config/axiosConfig";
import { PROYECTO_COLOR } from "../theme";
import RegistrarProyectoDialog from "../components/RegistrarProyectoDialog";

const Th = styled(TableCell)(({ theme }) => ({
  fontWeight: 700,
  fontSize: "0.74rem",
  padding: "2px 6px",
  lineHeight: 1.2,
  color: theme.palette.mode === "dark" ? theme.palette.common.white : PROYECTO_COLOR,
  backgroundColor: theme.palette.mode === "dark" ? theme.palette.grey[800] : `${PROYECTO_COLOR}14`,
}));

const formatFecha = (value) => {
  if (!value) return "-";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "-";
  return `${String(d.getDate()).padStart(2, "0")}/${String(d.getMonth() + 1).padStart(2, "0")}/${d.getFullYear()}`;
};

const formatMonto = (value) => {
  if (value === null || value === undefined || value === "") return "-";
  return `Bs${Number(value).toLocaleString("es-BO", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

// Mismo patrón de ordenamiento ya usado en AdministracionPersonalPage.jsx, con un set
// de columnas ordenables distinto por pestaña (Listado vs. Reporte).
const useOrdenado = (lista, valorOrden, sortConfig) => useMemo(() => {
  const { key, direction } = sortConfig;
  const getValue = valorOrden[key];
  if (!getValue) return lista;
  const factor = direction === "asc" ? 1 : -1;
  return [...lista].sort((a, b) => {
    const av = getValue(a);
    const bv = getValue(b);
    if (typeof av === "number" && typeof bv === "number") return (av - bv) * factor;
    return String(av ?? "").localeCompare(String(bv ?? ""), "es", { numeric: true, sensitivity: "base" }) * factor;
  });
}, [lista, valorOrden, sortConfig]);

const VALOR_ORDEN_LISTADO = {
  id: (r) => Number(r.id),
  nombre: (r) => r.nombre,
  descripcion: (r) => r.descripcion,
  ejecutivo_venta: (r) => r.ejecutivo_venta,
  nombre_servicio: (r) => r.nombre_servicio,
  fecha: (r) => (r.fecha ? new Date(r.fecha).getTime() : 0),
  estado: (r) => r.estado,
};

const VALOR_ORDEN_REPORTE = {
  id: (r) => Number(r.id),
  razon_social: (r) => r.razon_social,
  descripcion: (r) => r.descripcion,
  sucursal_facturar: (r) => r.sucursal_facturar,
  monto_contrato: (r) => Number(r.monto_contrato) || 0,
  potencia_instalada: (r) => Number(r.potencia_instalada) || 0,
  gestion: (r) => r.gestion,
  respaldo: (r) => r.respaldo,
  responsable_tecnico: (r) => r.responsable_tecnico,
  total_cobrado: (r) => Number(r.total_cobrado) || 0,
};

const ListadoTab = ({ proyectos, navigate }) => {
  const [sortConfig, setSortConfig] = useState({ key: "id", direction: "desc" });
  const ordenados = useOrdenado(proyectos, VALOR_ORDEN_LISTADO, sortConfig);

  const handleSort = (key) => {
    setSortConfig((prev) => ({ key, direction: prev.key === key && prev.direction === "asc" ? "desc" : "asc" }));
  };

  const SortableTh = ({ children, sortKey, align }) => (
    <Th align={align}>
      <TableSortLabel
        active={sortConfig.key === sortKey}
        direction={sortConfig.key === sortKey ? sortConfig.direction : "asc"}
        onClick={() => handleSort(sortKey)}
      >
        {children}
      </TableSortLabel>
    </Th>
  );

  return (
    <Paper variant="outlined">
      <TableContainer>
        <Table size="small" sx={{ "& td": { py: 0.15, px: 0.75, fontSize: "0.76rem", lineHeight: 1.2 } }}>
          <TableHead>
            <TableRow>
              <SortableTh sortKey="id">Cod</SortableTh>
              <SortableTh sortKey="nombre">Nombre</SortableTh>
              <SortableTh sortKey="descripcion">Descripción</SortableTh>
              <SortableTh sortKey="ejecutivo_venta">Ejecutivo de Ventas</SortableTh>
              <SortableTh sortKey="nombre_servicio">Servicio</SortableTh>
              <SortableTh sortKey="fecha" align="right">Fecha</SortableTh>
              <SortableTh sortKey="estado">Estado</SortableTh>
              <Th align="center">Ver</Th>
            </TableRow>
          </TableHead>
          <TableBody>
            {ordenados.map((p) => (
              <TableRow key={p.id} hover>
                <TableCell>{p.id}</TableCell>
                <TableCell>{p.nombre}</TableCell>
                <TableCell>{p.descripcion}</TableCell>
                <TableCell>{p.ejecutivo_venta || "-"}</TableCell>
                <TableCell>{p.nombre_servicio}</TableCell>
                <TableCell align="right">{formatFecha(p.fecha)}</TableCell>
                <TableCell>
                  <Chip
                    size="small" label={p.estado}
                    sx={{ fontSize: "0.64rem", height: 16, bgcolor: `${PROYECTO_COLOR}22`, color: PROYECTO_COLOR, fontWeight: 700 }}
                  />
                </TableCell>
                <TableCell align="center">
                  <Tooltip title="Ver detalle">
                    <IconButton size="small" onClick={() => navigate(`/proyectos/${p.id}`)} sx={{ color: PROYECTO_COLOR, p: 0.25 }}>
                      <VisibilityIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );
};

// Pestaña "Reporte" — a pedido del usuario, sin equivalente en el legacy (antes se
// llevaba en un Excel aparte). Mismos proyectos que el Listado, solo que mostrando las
// columnas del reporte (Monto Contrato/Potencia/Gestión/Respaldo/Responsable Técnico,
// más Cliente y Facturado, que ya se traían para otras pantallas).
const ReporteTab = ({ proyectos }) => {
  const [sortConfig, setSortConfig] = useState({ key: "", direction: "asc" });
  const [exportando, setExportando] = useState(false);
  const ordenados = useOrdenado(proyectos, VALOR_ORDEN_REPORTE, sortConfig);

  const handleSort = (key) => {
    setSortConfig((prev) => ({ key, direction: prev.key === key && prev.direction === "asc" ? "desc" : "asc" }));
  };

  const handleExportar = async () => {
    setExportando(true);
    try {
      const res = await axios.get("/api/proyectos/exportar-reporte", { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      }));
      const link = document.createElement("a");
      link.href = url;
      link.download = "reporte_proyectos.xlsx";
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    } catch {
      // El error se ve reflejado simplemente en que no se descarga nada — no hay
      // un lugar natural donde mostrar un Alert acá sin agregar más estado.
    } finally {
      setExportando(false);
    }
  };

  const SortableTh = ({ children, sortKey, align }) => (
    <Th align={align}>
      <TableSortLabel
        active={sortConfig.key === sortKey}
        direction={sortConfig.key === sortKey ? sortConfig.direction : "asc"}
        onClick={() => handleSort(sortKey)}
      >
        {children}
      </TableSortLabel>
    </Th>
  );

  return (
    <Paper variant="outlined">
      <Box sx={{ display: "flex", justifyContent: "flex-end", p: 1 }}>
        <Button
          size="small" startIcon={<DownloadIcon />} disabled={exportando}
          onClick={handleExportar} sx={{ color: PROYECTO_COLOR }}
        >
          {exportando ? "Generando..." : "Exportar Excel"}
        </Button>
      </Box>
      <TableContainer>
        <Table size="small" sx={{ "& td": { py: 0.15, px: 0.75, fontSize: "0.76rem", lineHeight: 1.2 } }}>
          <TableHead>
            <TableRow>
              <SortableTh sortKey="id" align="right">N°</SortableTh>
              <SortableTh sortKey="razon_social">Cliente</SortableTh>
              <SortableTh sortKey="descripcion">Detalle</SortableTh>
              <SortableTh sortKey="sucursal_facturar" align="center">Facturado</SortableTh>
              <SortableTh sortKey="monto_contrato" align="right">Monto Contrato</SortableTh>
              <SortableTh sortKey="total_cobrado" align="right">Total Cobrado</SortableTh>
              <SortableTh sortKey="potencia_instalada" align="right">Potencia Instalada W</SortableTh>
              <SortableTh sortKey="gestion" align="center">Gestión</SortableTh>
              <SortableTh sortKey="respaldo">Respaldo</SortableTh>
              <SortableTh sortKey="responsable_tecnico">Responsable Técnico</SortableTh>
            </TableRow>
          </TableHead>
          <TableBody>
            {ordenados.map((p) => (
              <TableRow key={p.id} hover>
                <TableCell align="right">{p.id}</TableCell>
                <TableCell>{p.razon_social}</TableCell>
                <TableCell>{p.descripcion}</TableCell>
                <TableCell align="center">{p.sucursal_facturar || "-"}</TableCell>
                <TableCell align="right" sx={{ fontWeight: 700 }}>{formatMonto(p.monto_contrato)}</TableCell>
                <TableCell align="right">{formatMonto(p.total_cobrado)}</TableCell>
                <TableCell align="right">{p.potencia_instalada || "-"}</TableCell>
                <TableCell align="center">{p.gestion || "-"}</TableCell>
                <TableCell>{p.respaldo || "-"}</TableCell>
                <TableCell>{p.responsable_tecnico || "-"}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );
};

const ProyectosListaPage = () => {
  const navigate = useNavigate();
  const [tab, setTab] = useState(0);
  const [proyectos, setProyectos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [abrirRegistrar, setAbrirRegistrar] = useState(false);

  const cargar = useCallback(() => {
    setLoading(true);
    setError("");
    axios.get("/api/proyectos")
      .then((res) => setProyectos(res.data?.data || []))
      .catch((e) => setError(e?.response?.data?.message || "Error al cargar los proyectos."))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { cargar(); }, [cargar]);

  return (
    <DashboardLayout>
      <PageHeader title="Proyectos" subtitle="Vinculación de facturas, egresos, compras y OT por proyecto" showBack={false} />
      <Box sx={{ p: 1 }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
          <Tabs
            value={tab}
            onChange={(_, v) => setTab(v)}
            sx={{
              minHeight: 34,
              "& .MuiTab-root": { minHeight: 34, py: 0.5, fontSize: 12, fontWeight: 700, textTransform: "none" },
              "& .MuiTab-root.Mui-selected": { color: PROYECTO_COLOR },
              "& .MuiTabs-indicator": { bgcolor: PROYECTO_COLOR },
            }}
          >
            <Tab label="Listado" />
            <Tab label="Reporte" />
          </Tabs>
          <Button
            variant="contained" onClick={() => setAbrirRegistrar(true)}
            sx={{ bgcolor: PROYECTO_COLOR, "&:hover": { bgcolor: "#4527a0" } }}
          >
            Registrar
          </Button>
        </Box>

        {error && <Alert severity="error" sx={{ mb: 1.5 }}>{error}</Alert>}

        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}><CircularProgress size={24} /></Box>
        ) : proyectos.length === 0 ? (
          <Alert severity="info">No existen registros.</Alert>
        ) : tab === 0 ? (
          <ListadoTab proyectos={proyectos} navigate={navigate} />
        ) : (
          <ReporteTab proyectos={proyectos} />
        )}
      </Box>

      <RegistrarProyectoDialog
        open={abrirRegistrar}
        onClose={() => setAbrirRegistrar(false)}
        onRegistrado={(id) => { setAbrirRegistrar(false); navigate(`/proyectos/${id}`); }}
      />
    </DashboardLayout>
  );
};

export default ProyectosListaPage;
